import React, { useState } from "react";
import { Users2, ChevronDown } from "lucide-react";
import { StatusBadge } from "../Common/StatusBadge";
import { SetupStatus, Team } from "../Common/types";
import { TeamId } from "store/redux/reducers/team/teamConstants";
import { InviteTeamModal } from "../Common/InviteTeamModal";
import { getTeamIdFromDisplayName } from "../FocusAreas/UseCase";
import { useOnboardingContext } from '../../../container/Layout/OnboardingContext/OnboardingContext';
import { Skeleton } from 'antd';

interface TeamMember {
    name: string;
    avatar: string;
}

interface SetupCardProps {
    id: string;
    title: string;
    icon: React.ReactNode;
    shortDesc: string;
    challenge: string;
    solution: string;
    benefitIcon: React.ReactNode;
    benefit: string;
    features: string[];
    teams: Team[];
    setupStatus: SetupStatus;
    setupTime: number;
    steps: number;
    completedSteps?: number;
    isSelected: boolean;
    isExpanded: boolean;
    beta?: boolean;
    addon?: boolean;
    onSelect: (id: string) => void;
    onExpand: (id: string, e: React.MouseEvent) => void;
    onInviteTeam?: (team: Team) => void;
    teamMembers?: TeamMember[];
}

export function SetupCard({
    id,
    title,
    icon,
    shortDesc,
    challenge,
    solution,
    benefitIcon,
    benefit,
    features,
    teams,
    setupStatus,
    setupTime,
    steps,
    teamMembers,
    completedSteps,
    isSelected,
    isExpanded,
    beta,
    addon,
    onSelect,
    onExpand,
    onInviteTeam
}: SetupCardProps) {
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState<TeamId | null>(null);
    const { isReady } = useOnboardingContext();

    const handleTeamClick = (team: string, e: React.MouseEvent) => {
        const teamId = getTeamIdFromDisplayName(team);
        setSelectedTeam(teamId);
        setShowInviteModal(true);
    };

    const handleInviteTeamMembers = async (emails: string[]) => {
        console.log("Emails received from modal:", emails);
        setShowInviteModal(false);
    };
    return (
        <div
            onClick={(e) => onExpand(id, e)}
            className={`
        bg-white rounded-xl shadow-sm transition-all duration-300
        ${isSelected ? "ring-2 ring-primaryColor" : "hover:shadow-md"}
        cursor-pointer group
      `}>
            <div className="p-4" onClick={() => onSelect(id)}>
                <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                        <div
                            className={`
              p-3 rounded-lg transition-colors duration-200 flex-shrink-0
              ${isSelected ? "bg-primaryColor text-white" : "bg-primaryExtremeLightColor text-primaryColor"}
              group-hover:${!isSelected ? "bg-primaryExtremeLightColor" : ""}
            `}>
                            {icon}
                        </div>
                        <div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
                                    {beta && (
                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-50 text-yellow-700">
                                            Beta
                                        </span>
                                    )}
                                    {addon && (
                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100">
                                            Addon
                                        </span>
                                    )}
                                </div>
                            </div>
                            <p className="text-gray-600 mb-2">{shortDesc}</p>
                            <div className="flex flex-wrap gap-2">
                                {teams.map((team) => (
                                    <div key={team} className="relative group">
                                        <div className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-primaryExtremeLightColor text-primaryColor border border-primaryExtremeLightColor  hover:bg-primaryLightColor hover:text-[#1556D0] ">
                                            <Users2 className="w-3 h-3 mr-1" />
                                            <button onClick={(e) => handleTeamClick(team, e)}>{team}</button>
                                            {onInviteTeam && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onInviteTeam(team);
                                                    }}>
                                                    {/* className="ml-2 p-0.5 hover:bg-primaryExtremeLightColor rounded transition-colors">
                                                    <UserPlus className="w-3 h-3" /> */}
                                                </button>
                                            )}
                                        </div>

                                        {/* Team Members */}
                                        {teamMembers && teamMembers.length > 0 && (
                                            <div className="absolute -bottom-2 left-2 flex -space-x-2">
                                                {teamMembers.slice(0, 3).map((member, idx) => (
                                                    <img
                                                        key={idx}
                                                        src={member.avatar}
                                                        alt={member.name}
                                                        className="w-5 h-5 rounded-full border-2 border-white"
                                                        title={member.name}
                                                    />
                                                ))}
                                                {teamMembers.length > 3 && (
                                                    <div className="w-5 h-5 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center text-[10px] text-gray-600 font-medium">
                                                        +{teamMembers.length - 3}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center space-x-3">
                        {isReady ? (
                            <StatusBadge
                                status={setupStatus}
                                setupTime={setupTime}
                                steps={steps}
                            />
                        ) : (
                            <Skeleton.Button active size="small" shape="round" style={{ width: 120 }} />
                        )}
                        <button
                            className={`
                p-1 rounded-full transition-transform duration-200
                ${isExpanded ? "rotate-180" : ""}
              `}>
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>
                </div>

                <div
                    className={`
          grid transition-all duration-300 ease-in-out
          ${isExpanded ? "grid-rows-[1fr] mt-4" : "grid-rows-[0fr]"}
        `}>
                    <div className="overflow-hidden">
                        <div className="border-t pt-4 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <h4 className="font-medium text-gray-900 mb-2">Challenge</h4>
                                    <p className="text-gray-600 text-sm">{challenge}</p>
                                </div>
                                <div>
                                    <h4 className="font-medium text-gray-900 mb-2">Solution</h4>
                                    <p className="text-gray-600 text-sm">{solution}</p>
                                </div>
                            </div>

                            <div>
                                <h4 className="font-medium text-gray-900 mb-2">Key Features</h4>
                                <div className="grid grid-cols-2 gap-2">
                                    {features.map((feature, index) => (
                                        <div key={index} className="flex items-center text-sm text-gray-600">
                                            <div className="w-1.5 h-1.5 rounded-full bg-primaryColor mr-2" />
                                            {feature}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="flex items-center text-sm text-primaryColor font-medium">
                                {benefitIcon}
                                <span className="ml-2">{benefit}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {showInviteModal && selectedTeam && (
                <InviteTeamModal
                    isOpen={showInviteModal}
                    onClose={() => setShowInviteModal(false)}
                    team={selectedTeam}
                    onInvite={handleInviteTeamMembers}
                />
            )}
        </div>
    );
}
